import { GetListParams } from 'react-admin';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import { CustomDataProvider } from '../../../providers/CustomDataProvider';
import { Resources } from '../../resource';

export type { PredictionMarketItem, PredictionEventItem } from './types';

const extractItems = (json: any) => {
  const rows = json?.items ?? json?.data?.items ?? json?.data ?? json ?? [];
  return Array.isArray(rows) ? rows : [];
};

const normalizeListItem = (item: any): any => {
  if (!item?.payload || (item.type !== 'EVENT' && item.type !== 'MARKET')) {
    return item;
  }
  const { payload, type, index } = item;
  if (type === 'EVENT') {
    return {
      ...payload,
      type,
      index,
      question: payload.customTitle || payload.title || '',
      customQuestion: payload.customTitle || '',
    };
  }
  // MARKET
  return { ...payload, type, index };
};

const toAcceptLanguage = (locale: string) => (locale === 'cn' ? 'zh' : locale);

export class PredictionFeaturedMarketsDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    const { filter = {}, pagination, meta } = params;
    const search = new URLSearchParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        search.append(key, value);
      }
    });

    // if (pagination?.perPage) {
    //   search.append('limit', pagination.perPage.toString());
    // }

    const headers = new Headers();
    if (typeof meta?.acceptLanguage === 'string' && meta.acceptLanguage.trim().length > 0) {
      headers.set('Accept-Language', toAcceptLanguage(meta.acceptLanguage));
    }

    const queryString = search.toString();
    const url = `${baseUrl}/${resource}${queryString ? `?${queryString}` : ''}`;
    const { json } = await httpClient(url, { method: 'GET', headers });
    const list = extractItems(json).map(normalizeListItem);

    return {
      data: list,
      total: json?.count ?? json?.data?.count ?? list.length,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: any): Promise<any> {
    const { id, meta } = params;
    const headers = new Headers();
    if (typeof meta?.acceptLanguage === 'string' && meta.acceptLanguage.trim().length > 0) {
      headers.set('Accept-Language', toAcceptLanguage(meta.acceptLanguage));
    }
    const { json } = await httpClient(`${baseUrl}/${resource}/${id}`, { method: 'GET', headers });
    const data = json?.data ?? json;
    return { data: { ...data, id: data?.id ?? id } };
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    if (resource !== Resources.predictionFeaturedMarkets) {
      return Promise.reject(new Error('Unsupported create resource'));
    }

    const payload =
      params?.data && typeof params.data === 'object' && !Array.isArray(params.data)
        ? params.data
        : { items: [] };

    const { json } = await httpClient(`${baseUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return {
      data: {
        id: 'featured-markets',
        markets: json?.data ?? json ?? payload,
      },
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async update(resource: string, params: any): Promise<any> {
    if (resource === Resources.predictionFeaturedMarkets) {
      return this.create(resource, params);
    }

    const isEventsResource = resource === Resources.predictionFeaturedMarketsEvents;
    if (!isEventsResource && resource !== Resources.predictionFeaturedMarketsMarkets) {
      return Promise.reject(new Error('Unsupported update resource'));
    }

    const itemId = params?.id ?? params?.data?.id;
    const question = params?.data?.question;
    const lang = params?.data?.lang;

    if (itemId === undefined || itemId === null || typeof question !== 'string') {
      return Promise.reject(new Error('Invalid update payload'));
    }

    const payload = isEventsResource
      ? {
          id: itemId,
          customTitle: question,
          lang,
        }
      : {
          id: itemId,
          customQuestion: question,
          lang,
        };

    const { json } = await httpClient(`${baseUrl}/${resource}/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });

    return {
      data: json?.data ?? json ?? payload,
    };
  }
}

export const predictionFeaturedMarketsDataProvider = new PredictionFeaturedMarketsDataProvider();
